import React, { useState, useEffect, useMemo, useCallback } from 'react';  
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';  
import layout from '../../../../Modules/layout.module.css';  
import styles from './DefineQuestions.module.css';  
import { CompleteQuestionnaireTemplateById, CompleteTemplateWithoutMandate, GetSectionsByTemplateId } from '../../../../API/TemplateBank/TemplateBank';  
import QuestionsView from './QuestionsView/QuestionsView';  
import SectionsDrawer from '../../../../Components/SectionsDrawer/SectionsDrawer';  
import { DrawerProvider } from '../../../../Components/SectionsDrawer/DrawerContext';  
import { ICriteriaDrawerSection, IDrawerItem, IDrawerSection } from '../../../../Types/Drawer/ISectionsDrawerItem';  
import Button from '../../../../Components/Utils/Button/Button';  
import { getClient, getClientRolePermission, getClientHasThirdPartySubsets } from '../../../../Redux/Store/Slices/client';
import { useAppSelector } from '../../../../Redux/Store/Store';
import { isSetDate } from '../../../../Helpers/Dates';
import { useWorkflowContext } from '../../../../Hooks/useWorkflowContext';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import ConfirmAction from '../../../../Components/Utils/ConfirmAction/ConfirmAction';
  
type DefineQuestionsProps = object;  
  
const DefineQuestions = ({}: DefineQuestionsProps) => {  

  const { templateId, templateTitle, sectionId, criteriaId } = useParams();  
  const { getWorkflowPath, isAdminContext } = useWorkflowContext();

  const navigate = useNavigate();  
  const [sections, setSections] = useState<ICriteriaDrawerSection[]>([]);  

  const [confirmNonAIVisible, setConfirmNonAIVisible] = useState(false);

  const client = useAppSelector(getClient);
  const rolePermissions = useAppSelector(getClientRolePermission);
  const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);
  
  useEffect(() => {  
    const getSections = async () => {  
      if (templateId && client) {  
        const response = await GetSectionsByTemplateId(templateId, client.id);  
        const drawerSections: ICriteriaDrawerSection[] = response.map((section) => ({  
          id: section.id,  
          title: section.title,  
          status: "Not Set",  
          items: section.criteria.map(item => ({  
            id: item.id,  
            title: item.title,  
            url: `/${section.id}/${item.id}`,  
            checked: isSetDate(item.date),
            fullCriteria: item.fullCriteria
          }))  
        }));  
        setSections(drawerSections);  
      }  
    };  
    getSections();  
  }, [templateId]);  
  
  useEffect(() => {  
    if (sections.length > 0 && sections[0].items.length > 0 && !sectionId && !criteriaId) {  
        navigate(getWorkflowPath(templateId || '', templateTitle || '', `define-questions/${sections[0].id}/${sections[0].items[0].id}`));  
    }  
  }, [sections]);  

  // Compute currently selected section & criteria (used for value prop)
  const selectedSection = useMemo(
    () => sections.find(s => s.id === sectionId),
    [sections, sectionId]
  );
  
  const selectedCriteria = useMemo(
    () => selectedSection?.items.find(i => i.id === criteriaId),
    [selectedSection, criteriaId]
  );

  const isLastItem = () => {  
      const currentSectionIndex = sections.findIndex(section => section.id === sectionId);  
      if (currentSectionIndex === -1) return false;  
    
      const currentSection = sections[currentSectionIndex];  
      const currentCriteriaIndex = currentSection.items.findIndex(item => item.id === criteriaId);  
      if (currentCriteriaIndex === -1) return false;  
    
      const isLastCriteriaInSection = currentCriteriaIndex === currentSection.items.length - 1;  
      const isLastSection = currentSectionIndex === sections.length - 1;  
    
      return isLastCriteriaInSection && isLastSection;  
  };  
    
  const getNextItemTitle = () => {  

    if (isLastItem()) {  
        return 'Additional Information';  
    }  

      const currentSectionIndex = sections.findIndex(section => section.id === sectionId);  
      if (currentSectionIndex === -1) return '';  
    
      const currentSection = sections[currentSectionIndex];  
      const currentCriteriaIndex = currentSection.items.findIndex(item => item.id === criteriaId);  
      if (currentCriteriaIndex === -1) return '';  
    
      const nextCriteriaIndex = currentCriteriaIndex + 1;  
      if (nextCriteriaIndex < currentSection.items.length) {  
          return currentSection.items[nextCriteriaIndex].title;  
      } else {  
          const nextSectionIndex = currentSectionIndex + 1;  
          if (nextSectionIndex < sections.length) {  
              const nextSection = sections[nextSectionIndex];  
              if (nextSection.items.length > 0) {  
                  return nextSection.items[0].title;  
              }  
          }  
      }  
      return '';  
  };  
    
  const nextHandler = () => {  
      if (isLastItem()) {  
          // Navigate to additional information instead of completing
          navigate(getWorkflowPath(templateId || '', templateTitle || '', 'additional-information'));
          return;  
      }  
    
      const nextTitle = getNextItemTitle();   
    
      const currentSectionIndex = sections.findIndex(section => section.id === sectionId);  
      if (currentSectionIndex === -1) return;  
    
      const currentSection = sections[currentSectionIndex];  
      const currentCriteriaIndex = currentSection.items.findIndex(item => item.id === criteriaId);  
      if (currentCriteriaIndex === -1) return;  
    
      const nextCriteriaIndex = currentCriteriaIndex + 1;  
      if (nextCriteriaIndex < currentSection.items.length) {  
          const nextCriteria = currentSection.items[nextCriteriaIndex];  
          navigate(getWorkflowPath(templateId || '', templateTitle || '', `define-questions/${currentSection.id}/${nextCriteria.id}`));  
      } else {  
          const nextSectionIndex = currentSectionIndex + 1;  
          if (nextSectionIndex < sections.length) {  
              const nextSection = sections[nextSectionIndex];  
              if (nextSection.items.length > 0) {  
                  const nextCriteria = nextSection.items[0];  
                  navigate(getWorkflowPath(templateId || '', templateTitle || '', `define-questions/${nextSection.id}/${nextCriteria.id}`)); 
              }  
          }  
      }  
  };  

const fullCriteriaChanged = useCallback(async (newCriteria: string) => {
  if (!sectionId || !criteriaId) return;

  setSections(prev =>
    prev.map(section =>
      section.id === sectionId
        ? {
            ...section,
            items: section.items.map(item =>
              item.id === criteriaId ? { ...item, fullCriteria: newCriteria } : item
            )
          }
        : section
    )
  );

   const response = await axiosInstance.post(`/UpdateCriteriaBankFullCriteria/${templateId}/${criteriaId}/${client?.id}`, newCriteria);
}, [sectionId, criteriaId]);
  
  return (  
    <div className={`${layout.container} ${styles.pageShell}`}>  
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderEyebrow}>Template Questions</div>
        <h1 className={styles.pageTitle}>{templateTitle}</h1>
        <p className={styles.pageSubtitle}>Create questions for this diagnostic</p>
      </div>

      <div className={styles.contentLayout}>  
      <div className={styles.mainColumn}>  
        <Routes>  
          <Route path={`/:sectionId/:criteriaId`} element={<QuestionsView criteriaChangedHandler={fullCriteriaChanged} fullCriteria={selectedCriteria?.fullCriteria} />} />  
        </Routes>  
        <div className={`${layout.column1} ${layout.container}`}>  
            <div className={styles.footerLabel}>{`Next: ${getNextItemTitle()}`}</div>
          <div className={`${layout.rowSpan} ${styles.footerBar}`}>  
            <Link to={getWorkflowPath(templateId || '', templateTitle || '', 'define-structure')}>  
              <Button>Back: Define Structure</Button>  
            </Link>  
            <Button clickHandler={nextHandler}>{isLastItem()? `Next: Additional Information` : `Next Criteria`}</Button>  
          </div>  
        </div>  
      </div>  

      {sectionId && criteriaId && (
        <div className={styles.drawerPanel}>
          <div className={styles.drawerPanelHeader}>
            <p className={styles.drawerPanelTitle}>Criteria Navigator</p>
            <p className={styles.drawerPanelHint}>Move between sections and criteria while building each questionnaire.</p>
          </div>
          <SectionsDrawer 
            sections={sections} 
            basePath={getWorkflowPath(templateId || '', templateTitle || '', 'define-questions')} 
            activeSectionId={sectionId}
            activeItemId={criteriaId}
          ></SectionsDrawer>
        </div>
      )}
    </div>
    </div>  
  );  
};  
  
export default DefineQuestions;  